/**
 * HlsStream - The Public API to Bam-Hls
 */
'use strict';

import Events from './HlsEvents';
import States from './HlsStates';
import Errors from './HlsErrors';
import ErrorStrings from './HlsErrorStrings';
import PlaybackStates from './PlaybackStates';
import MediaStates from './MediaStates';

import HlsController from './HlsController';
import Utils from './HlsUtils';
import DataTypes from './HlsDataTypes';
import KeySystems from './KeySystems';
import CencModes from './CencModes';

/**
 * Supports control, playback and monitoring of a single HLS stream to a single video element
 *
 * It provides access to all properties, events and methods which are used to monitor,
 * control and affect playback of a single HLS stream.
 *
 * The intent is that all control of the video element and HLS stream will be handled via the
 * HlsStream interface.
 *
 * @example
 * if( HlsStream.isSupported() ) {
 *     hls = new HlsStream();
 *     hls.loadSource('http://www.streambox.fr/playlists/x36xhzz/x36xhzz.m3u8');
 *     hls.attachMedia(document.getElementById('video'));
 *     hls.play();
 * }
 *
 * @public
 */
class HlsStream {
	/**
	* The current version of the player at build time, assuming versionify was run.
	*
	* This should be a semantic version, ie semver
	*
	* If versionify was not run the value will be \__VERSION__
	*
	* @since 0.0.1
	* @type {string} -
	*/
	static get version() {
		return '__VERSION__';
	}

	/**
	* The current number of active HlsStresm instances
	*
	* @since 2.9
	* @type {integer} -
	*/
	static get activeStreamCount() {
		return HlsController.totalActive();
	}


	/**
	* If the current browser supports redirects
	*
	* @since 2.9
	* @type {boolean} -
	*/
	static get isRedirectSupported() {
		return HlsController.isRedirectSupported();
	}

	/**
	* Is it possible to load and play Hls using the current browser environment with this
	* HlsStream implementation?
	*
	* In order for HLS playback and loading to be possible a number of pre-requisites
	* must be met, including the availability of basic H.264/AAC codec support as well as the
	* MSE capabality on the media element
	*
	* If playback is not supported, you will not be able to call loadSource() or attachMedia(),
	* thus leaving not much point.
	*
	* @example
	* if( HlsStream.isSupported() ) {
	*     // instantiate HlsStream
    * }
	*
	* @since 0.0.1
	* @returns {boolean} is playback supports in this environment?
	*/
	static isSupported() {
		return HlsController.isSupported();
	}

	/**
	* Events enumeration, used with on/off/once methods
	*
	* All the events which are emitted during operation. Some events
	* are intended for intenral use only.
	*
	* @example
	* hls.on( HlsStream.Events.STREAM_PLAYBACK, () => {
	*     //the stream is now in the playback state
	* });
	*
	* @since 0.0.1
	* @type {enumeration}
	*/
	static get Events() {
		return Events;
	}

	/**
	* Errors enumeration, see STREAM_ERROR event
	*
	* @example
	* if(theError.error === HlsStream.Errors.LOAD_FAILED) {
	*     // loading failed
	* }
	*
	* @type {enumeration} -
	*
	* @since 0.2
	*/
	static get Errors() {
		return Errors;
	}

	/**
	 * Error message strings enumeration, see STREAM_ERROR event
	 *
	 * @type {enumeration}
	 *
	 * @since 1.4
	 */
	static get ErrorStrings() {
		return ErrorStrings;
	}

	/**
	* States enumeration, used with the state property
	*
	* All the possible player states
	*
	* @example
	* if(hls.state === HlsStream.States.LOADING) {
	*     //the player is currently loading
	* }
	*
	* @type {enumeration}
	*
	* @since 0.2
	*/
	static get States() {
		return States;
	}

	/**
	* Playback States enumeration, used with the playbackState property
	*
	* All the possible playback states
	*
	* @example
	* if(hls.playbackState === HlsStream.PlaybackStates.PLAYING) {
	*     //the video is currently playing
	* }
	*
	* @since 0.2
	*/
	static get PlaybackStates() {
		return PlaybackStates;
	}

	/**
	* Media States enumeration, used with the mediaState property
	*
	* All the possible media states
	*
	* @example
	* if(hls.mediaState === HlsStream.MediaStates.DETACHED) {
	*     //the media is currently detached
	* }
	*
	* @type {enumeration}
	*
	* @since 0.3
	*/
	static get MediaStates() {
		return MediaStates;
	}

	/**
	 * Data types enumeration, used with the xhrSetupCallback property
	 *
	 * All the possible data types of objects that can be loaded
	 *
	 * @example
	 * hls.xhrSetupCallback = function (xhr, url, type) {
	 * 	if (url){
	 * 		if (type === HlsStream.DataTypes.KEY) {
	 * 			// the requested URL is a key call
	 * 		}
	 * 	}
	 * 	return xhr;
	 * }
	 *
	 * @type {enumeration}
	 * @since 0.10
	 */
	static get DataTypes() {
		return DataTypes;
	}

	/**
	 * Key systems enumeration
	 *
	 * All of the possible key systems
	 *
	 * @type {enumeration}
	 * @since 3.2
	 */
	static get KeySystems() {
		return KeySystems;
	}

	/**
	 * @type {enumeration}
	 * @since 3.2
	 */
	static get CencModes() {
		return CencModes;
	}

	/**
	 * Constructs an instance of an HlsStream
	 *
	 * @param {object} [config={}] - configuration object. Properties matching setters will be applied.
	 *
	 * @example
	 * hls = new HlsStream({
	 *     autoPlay: true,
	 *     debug: true,
	 * });
	 *
	 * @since 0.0.1
	 */
	constructor(config = {}) {
		this._controller = new HlsController({
			disableWorkarounds : !!config.disableWorkarounds,
			disablePromiseOverloadCheck: !!config.disablePromiseOverloadCheck,
			decryptOutsideWebworker : !!config.decryptOutsideWebworker,
		});
		this._disposed = false;

		// config properties will ONLY be applied to setters.
		this.applyConfig(config);
	}

	/**
	 * release all resources used by HlsStream instance.
	 *
	 * The event list will be released, the media will be detached.
	 *
	 * It is an error to use the HlsStream instance once STREAM_DISPOSED has
	 * been emitted
	 *
	 * @since 0.0.4
	 */
	dispose() {

		// this issue is that events may fire after calling dispose(), and those
		// events may have handlers which themselves call HlsStream methods.
		// the final event is always STREAM_DISPOSED, and that is when we close
		// the gate.
		if( !this._disposed ) {
			this._disposed = true;

			this.once(Events.STREAM_DISPOSED, () => {
				this._controller = null;
			});

			this._controller.dispose();
		}
	}

	/**
	* registers a listener for an event.
	*
	* When the event is triggered, the callback function will be called
	*
	* Use `off` to unregister. Use `once` for a once-off registration
	*
	* @example
	* hls.on( HlsStream.Events.STREAM_LOADED, function () {
	*     //STREAM_LOADED was emitted
	* });
	*
	* @param {HlsStream.Events.EVENT} event - the event to register
	* @param {function} func - the function to call when event is emitted
	*
	* @since 0.0.4
	*/
	on(event, func) {
		this._controller.on(event, func);
	}

	/**
	* deregisters a listener for an event. func must === the func used to register
	* the event in the first place
	*
	* If there is no matching event handler, then nothing happens.
	*
	* @example
	* hls.off( HlsStream.Events.STREAM_LOADED, myStreamLoadedHandler );
	*
	* @param {HlsStream.Events.EVENT} event - the event to deregister
	* @param {function} func - the function that was registered previously
	*
	* @since 0.0.6
	*/
	off(event, func) {
		this._controller.off(event, func);
	}

	/**
	* registers a one-shot listener for an event. The listener is automatically Removed
	* once the event fires.
	*
	* @example
	* hls.once( HlsStream.Events.STREAM_SEEKING_FINISHED, () => {
	*     //the stream seeked!
	* });
	*
	* @param {HlsStream.Events.EVENT} event - the event to register
	* @param {function} func - the function to invoke on the event
	*
	* @since 0.0.6
	*/
	once(event, func) {
		this._controller.once(event, func);
	}

	/**
	* begin prefetching if the stream is in the LOADED state, or if the stream is not LOADED yet,
	* then queue the prefetch.
	*
	* This essentially means that the player will move into the FETCHING state after
	* entering the LOADED state, or if it is already in the LOADED state will immeidately
	* move into the FETCHING state. If a media is ATTACHED, then it will proceed to the
	* PLAYBACK state after STREAM_CAN_PLAY is emitted.
	*
	* The FETCHING state is where the player begins downloading media content.
	*
	* This only has an effect if autoPrefetch is false, otherwise the player will automatically
	* prefetch.
	*
	* This capability might be useful if you wanted to pre-emptively load an HLS stream,
	* but did not want to buffer it, or begin downloading.
	*
	* @example
	* if( hasCurrentHlsStreamFinished()) {
	*     nextHlsStream.prefetch();
	* }
	*
	* @since 0.0.1
	*/
	prefetch() {
		this._controller.prefetch();
	}

	/**
	 * halt fetching fragments if the stream is has loaded and is currently fetching.
	 *
	 * This does not halt the loading of variants, and as such live streams will continue to update the chosen
	 * variant, updating the fragment list and moving the sliding window as needed.
	 *
	 * This may be useful if you want to limit the amount of data the user downloads if they have the stream
	 * paused for a long time.
	 *
	 * @since 1.12
	 */
	stopFetching() {
		this._controller.stopFetching();
	}

	/**
	 * resumes fetching fragments if the stream has loaded and has previously stopped fetching.
	 *
	 * This does not trigger the loading of variants. If you're starting the stream, prefetch() should be used instead.
	 *
	 * @since 1.12
	 */
	resumeFetching() {
		this._controller.resumeFetching();
	}

	/**
	* pauses the player. If the player is not in the PLAYBACK state, the player will
	* enter the PAUSED playbackState when it enters the PLAYBACK state.
	*
	* `paused` will return true.
	*
	* The player is always either paused or not paused. If it is capable of PLAYBACK,
	* ie, the mediaState is ATTACHED and the source is sufficiently downloaded, then
	* the player will enter the PLAYBACK state, and the PAUSED playbackState.
	*
	* If the player is the PLAYING or BUFFERING playbackStates and you call `pause()` the player
	* will immediately pause and return to the PAUSED state. The player will continue fetching,
	* ie, if the player was BUFFERING, BUFFERING will continue in the background, Although
	* the playbackState will no longer be BUFFERING and the STREAM_BUFFERING_FINISHED event
	* will fire.
	*
	* Should have no effect in the ENDED playbackState
	*
	* @example
	* if( userPressedPauseButton()) {
	*     hls.pause();
	* }
	*
	* @since 0.0.1
	*/
	pause() {
		this._controller.pause();
	}

	/**
	* unpauses the player. If the player is not in the PLAYBACK state, the player will enter the
	* PLAYING playbackState when sufficient content is buffered and the media is ATTACHED
	*
	* `paused` will return false.
	*
	* The player is always either paused or not paused. If it is capable of PLAYBACK,
	* ie, the mediaState is ATTACHED and the source is sufficiently downloaded, then
	* the player will enter the PLAYBACK state. If enough content is buffered to play, then
	* the player will enter the PLAYING playbackState. If enough content is not available to play,
	* then the player will enter the BUFFERING playbackState, and transition to the PLAYING playbackState
	* when BUFFERING has finished and it can begin PLAYING, ie STREAM_BUFFERING_FINISHED event has fired.
	*
	* If the player is not paused, and a seek occurs, the player will continue PLAYING after the seek
	* has completed.
	*
	* Should have no effect in the ENDED playbackState
	*
	* @example
	* if( userPressedPlayButton()) {
	*     hls.play();
	* }
	*
	* @since 0.0.1
	*/
	play() {
		this._controller.play();
	}

	/**
	* toggles the paused/playing state of the player.
	*
	* If the player is paused, it will play(), if its not paused, it will pause()
	*
	* @example
	* if( userPressedPlayPauseButton()) {
	*     hls.playPause();
	* }
	*
	* @since 0.0.4
	*/
	playPause() {
		this._controller.playPause();
	}

	/**
	* seeks the player to the time specified in seconds.
	*
	* The player needs to be in the PLAYBACK state for this to have an
	* effect.
	*
	* It is equivalent to setting the currentTime property to seconds.
	*
	* If the player is paused, the player will return to the PAUSED
	* playbackState after the seek is completed (ie when STREAM_SEEKING_FINISHED is
	* emitted). If the player is !paused, it will return to either PLAYING or BUFFERING
	* and then PLAYING playbackState after the seek has completed
	*
	* In the ENDED playbackState seek() can be used to restart video playback.
	*
	* @example
	* hls.seek(5*60); // seek 5 minutes into the video
	*
	* @since 0.0.5
	* @param {number} seconds - how many seconds to seek into the video from the start
	*/
	seek(seconds) {
		this._controller.seek(seconds);
	}

	/**
	* seeks the player to the specified time
	*
	* The manifest needs to have a PDT and the player needs to have loaded at least one variant
	* for this to have an effect.
	*
	* This is a helper function; seeking is handled the same way as the normal seek
	* once the offset is calculated.
	*
	* In the ENDED playbackState seek() can be used to restart video playback.
	*
	* @example
	* hls.seekByPdt('Thu May 19 2016 07:22:55 GMT+1000');
	*
	* @since 0.7
	* @param {Date} time - desired date/time to seek to
	*/
	seekByPdt(time) {
		this._controller.seekByPdt(time);
	}

	/**
	* seeks the player to the live point.
	*
	* It's better to use liveStreamsStartAtLiveTime for an intial seek to live, to
	* avoid an initial seek.
	*
	* Does not function if isLive is false.
	*
	* @since 0.4
	*/
	seekToLive() {
		this._controller.seekToLive();
	}

	/**
	* attaches a video element to the player and sets up the MSE media chain.
	*
	* The player takes control of the video element provided and establishes a
	* transmuxing, remuxing pipeline which culminates in a Media Stream source
	* feeding into the video element.
	*
	* This takes a relatively long time, thus the media is only attached, once
	* the MEDIA_ATTACHED event has fired.
	*
	* @emit {MEDIA_ATTACHED} when the media is attached
	*
	* Currently, if you detach a media element, the HlsStream is no longer
	* usable.
	*
	* Calling with null is the same as calling detachMedia()
	*
	* @example
	* hls.attachMedia(document.getElementById('video'));
	*
	* @since 0.0.1
	* @param {video} video - the video element to attach to the HLS player.
	*/
	attachMedia(video) {
		this._controller.attachMedia(video);
	}

	/**
	* detaches the current video element from the player and destroys the MSE chain.
	*
	* @emits {MEDIA_DETACHED} when the media is detached
	*
	* Currently, if you detach a media element, the HlsStream is no longer
	* usable. You should either destroy the hlsStream instance or detach the media
	* before attaching the same video element to another HlsStream isntance.
	*
	* @example
	* hls.detachMedia();
	*
	* @since 0.0.1
	*/
	detachMedia() {
		this._controller.detachMedia();
	}

	/**
	* returns true if the media is attached.
	*
	* @emits {MEDIA_ATTACHED} when the media is detached
	* @emits {MEDIA_DETACHED} when the media is detached
	*
	* @since 0.2
	* @type {boolean}
	*/
	get isMediaAttached() {
		return this._controller.isMediaAttached;
	}

	/**
	* begins loading the specified HLS stream.
	*
	* The player will begin loading the HLS url supplied. It will transition to the
	* LOADING state. If the url is valid, and downloads and parses successfully, it
	* will transition to the LOADED state.
	*
	* If autoPrefetch, or autoPlay are enabled, or play() has been called, or prefetch()
	* it will then transition into the FETCHING state.
	*
	* Once the media has been attached with attachMedia, it will transition into the
	* PLAYBACK state.
	*
	* @emits {STREAM_LOADING} when the stream begins loading
	* @emits {STREAM_LOADED} once the stream is succesfully LOADED
	* @emits {STREAM_FETCHING} once the stream begins FETCHING
	* @emits {STREAM_PLAYBACK} once the stream enters the PLAYBACK state after a media has
	* been attached
	* @emits {STREAM_ERROR} if an error occurs during LOADING.
	* @example
	* hls.loadSource('http://www.streambox.fr/playlists/x36xhzz/x36xhzz.m3u8');
	*
	* @since 0.0.1
	* @param {string} url - the hls url to load, ie an .m3u8
	*/
	loadSource(url) {
		this._controller.loadSource(url);
	}

	/**
	 * Flushes the stream buffer.
	 *
	 * The flush behaves differently based on the value of levelFlush, if false it will flush all fragments ahead of
	 * the current time, if true it will flush all fragments of lower bitrate variants than current. Regardless of
	 * setting the currently playing fragment and the next will not be flushed, to prevent buffer stalls.
	 *
	 * Returns true if the buffer was successfully flushed. Returns false if failed. A failed buffer flush typically
	 * means that no variants have had their fragment data loaded yet.
	 *
	 * Note that backfill is not currently implemented so seeking backwards after a level based flush may cause stalls.
	 *
	 * @since 1.0
	 * @param {boolean} levelFlush - if true, only flushes fragments that were loaded from a variant lower than
	 * chosenVariant
	 */
	flushBuffer(levelFlush) {
		return this._controller.flushBuffer(levelFlush);
	}

	/**
	* returns the player's state
	*
	* - UNLOADED
	* - LOADING
	* - LOADED
	* - FETCHING
	* - PLAYBACK
	*
	* @emits {STREAM_STATE_CHANGED} when the state changes.
	*
	* @since 0.2
	* @type {HlsStream.States.STATE}
	*/
	get state() {
		return this._controller.state;
	}

	/**
	* returns the player's playback state
	*
	* If the player is not in state PLAYBACK, then will returns
	* NOTREADY
	*
	* - NOTREADY
	* - PAUSED
	* - BUFFERING
	* - PLAYING
	* - SEEKING
	* - ENDED
	*
	* @emits {STREAM_PLAYBACKSTATE_CHANGED} when the playbackState changes.
	*
	* @since 0.2
	* @type {HlsStream.PlaybackStates.PLAYBACKSTATE}
	*/
	get playbackState() {
		return this._controller.playbackState;
	}

	/**
	* returns the player's media state
	*
	* - DETACHED
	* - ATTACHING
	* - ATTACHED
	*
	* @emits {STREAM_MEDIASTATE_CHANGED} when the mediaState changes.
	*
	* @since 0.3
	* @type {HlsStream.MediaStates.MEDIASTATE}
	*/
	get mediaState() {
		return this._controller.mediaState;
	}

	/**
	* the media position, in seconds, that the player will start playback at after loading
	*
	* If you want to start the stream at anywhere other than the beginning, It is more efficient
	* to provide a startTime, than it is to wait for the media to load at the beginning and then
	* seek() to a different position
	*
	* The startTime needs to be set before the stream enters the FETCHING state. It has no
	* effect once the FETCHING state has been entered.
	*
	* @since 0.0.1
	* @type {number}
	*/
	get startTime() {
		return this._controller.startTime;
	}

	set startTime(seconds) {
		this._controller.startTime = seconds;
	}

	/**
	 * the media position that the player will start playback at after loading
	 *
	 * The manifest must have a PDT for this to have an effect. The start time will be set as soon as the PDT is known.
	 *
	 * startTimeByPdt will override startTime.
	 *
	 * @since 1.10
	 * @type {Date}
	 */
	get startTimeByPdt() {
		return this._controller.startTimeByPdt;
	}

	set startTimeByPdt(time) {
		this._controller.startTimeByPdt = time;
	}

	/**
	* the variant to select for intitial playback, an index into the variants
	* array, -1 for the default variant
	*
	* A variant is a version of the stream encoded at a given bitrate. There are
	* multiple variants in the stream in order to allow bitrate adaption.
	*
	* The default variant is the first variant in the manifest, as specified by the
	* server.
	*
	* You can effect which variant is selected for stream startup by specifying the
	* startVariant
	*
	* The bitrate controller may choose a variant that does not match the variant
	* you requested in order to meet the various other constraints/requests that
	* may be placed on it.
	*
	* Use 0 if you want the minimum variant, as the variant list is sorted by
	* bitrate.
	*
	* If startVariant is higher than the highest number variant, the highest numbered
	* variant will be selected.
	*
	* startVariant is overriden by startBitrate. It overrides minimumBitrate and maximumBitrate,
	* but only for the first fragment.
	*
	* @since 0.9
	* @type {integer}
	*/
	get startVariant() {
		return this._controller.startVariant;
	}

	set startVariant(value) {
		this._controller.startVariant = value;
	}

	/**
	* the bitrate to select for intitial playback, in bits per second, -1 to use
	* the default bitrate
	*
	* The default bitrate is the first bitrate in the manifest, as specified by the
	* server.
	*
	* You can effect which bitrate is selected by specifying the startBitrate, and
	* the bitrate controller will select the bitrate which is closest to the bitrate you
	* specify.
	*
	* Use 0 if you want the minimum bitrate.
	*
	* startBitrate overrides startVariant. It also overrides minimumBitrate and maximumBitrate,
	* but only for the first fragment.
	*
	* @since 0.9
	* @type {integer}
	*/
	get startBitrate() {
		return this._controller.startBitrate;
	}

	set startBitrate(value) {
		this._controller.startBitrate = value;
	}

	/**
	* if true, the player will automatically move into the FETCHING state once the stream is
	* LOADED (ie STREAM_LOADED has fired).
	*
	* Defaults to true.
	*
	* Has no effect after STREAM_LOADED has fired.
	*
	* @since 0.0.4
	* @type {boolean}
	*/
	get autoPrefetch() {
		return this._controller.autoPrefetch;
	}

	set autoPrefetch(value) {
		this._controller.autoPrefetch = value;
	}

	/**
	* if true, the player will automatically move into the FETCHING state once the stream is
	* LOADED (ie STREAM_LOADED has fired), and then into the PLAYBACK state once the media
	* is attached.
	*
	* Defaults to false.
	*
	* Has no effect after STREAM_PLAYBACK has fired.
	*
	* @since 0.0.4
	* @type {boolean}
	*/
	get autoPlay() {
		return this._controller.autoPlay;
	}

	set autoPlay(value) {
		this._controller.autoPlay = value;
	}

	/**
	 * If true, the player will automatically reload the stream if the media element throws an error.
	 *
	 * Defaults to true.
	 *
	 * @since 1.13
	 * @type {boolean}
	 */
	get autoRecover() {
		return this._controller.autoRecover;
	}

	set autoRecover(value) {
		this._controller.autoRecover = value;
	}

	/**
	 * If true, the player will attempt to force the stream to continue playing if playback stalls.
	 *
	 * In older versions of bam-hls, it was possible for playback to become stuck, and require the player to nudge the
	 * playhead forward a small amount of time to unstick playback. This is no longer needed, due to our MuxJS demuxer.
	 *
	 * Defaults to false.
	 *
	 * @since 2.3
	 * @type {boolean}
	 */
	get nudgeOnStall() {
		return this._controller.nudgeOnStall;
	}

	set nudgeOnStall(value) {
		this._controller.nudgeOnStall = value;
	}

	/**
	 * if true, and the stream is live, the stream will start at the liveTime
	 *
	 * @since 0.8
	 * @type {boolean}
	 */
	get liveStreamsStartAtLiveTime() {
		return this._controller.liveStreamsStartAtLiveTime;
	}

	set liveStreamsStartAtLiveTime(value) {
		this._controller.liveStreamsStartAtLiveTime = value;
	}

	/**
	* the currently loaded hls url.
	*
	* setting is equivalent to calling loadSource(url)
	*
	* @since 0.3
	* @type {?url}
	*/
	get source() {
		return this._controller.src;
	}

	set source(url) {
		this._controller.src = url;
	}



	/**
	* the currently attached media.
	*
	* setting is equivalent to calling attachMedia(video)
	*
	* @since 0.1
	* @type {?video}
	*/
	get media() {
		return this._controller.media;
	}

	set media(video) {
		this._controller.media = video;
	}

	/**
	* the current media position, in seconds, from the start
	*
	* setting is equivalent to calling seek(seconds)
	*
	* It can be useful to use for implemnting jump-forward capability
	*
	* @example
	* hls.currentTime += 30; // skip 30 seconds forward
	*
	* @since 0.0.5
	* @type {number}
	*/
	get currentTime() {
		return this._controller.currentTime;
	}

	set currentTime(seconds) {
		this._controller.currentTime = seconds;
	}

	/**
	* the current media position, as a date
	*
	* setting is equivalent to calling seekByPdt(time)
	*
	* The manifest needs to have a PDT and the player needs to have loaded at least one variant
	* for this to have an effect.
	*
	* Returns null if the stream does not have a base PDT
	*
	* @since 2.12
	* @type {Date}
	*/
	get currentTimeAsPdt() {
		return this._controller.currentTimeAsPdt;
	}

	set currentTimeAsPdt(time) {
		this._controller.currentTimeAsPdt = time;
	}

	/**
	 * Playback rate of the stream.
	 *
	 * Default is 1.0. The higher the value, the faster the stream will play.
	 *
	 * Only values greater than 0.0 are supported. Reverse playback is not yet implemented. pause() should be used
	 * instead of trying to set the playback rate to 0.0.
	 *
	 * @since 0.10
	 * @type {number}
	 */
	get playbackRate() {
		return this._controller.playbackRate;
	}

	set playbackRate(value) {
		this._controller.playbackRate = value;
	}

	/**
	* is the stream loaded? Has STREAM_LOADED fired.
	*
	* A stream being loaded means that it has successfully download and parsed
	* the manifest, and has not had a following error to cause it to become
	* UNLOADED.
	*
	* @emits {STREAM_LOADED} when the stream loads successfully
	* @emits {STREAM_UNLOADED} when the stream is unloaded, possibly due to error
	*
	* @since 0.2
	* @type {boolean}
	*/
	get isLoaded() {
		return this._controller.loaded;
	}

	/**
	* is the stream fetching?
	*
	* The stream will be fetching once it has entered the FETCHING phase, until
	* the source is unloaded or the HlsStream is destroyed. The stream continues
	* to be fetching while the state is in the FETCHING or PLAYBACK states, even if
	* the playbackState is ENDED.
	*
	* @emits {STREAM_FETCHING} when the stream begins fetching
	*
	* @since 0.2
	* @type {boolean}
	*/
	get isFetching() {
		return this._controller.fetching;
	}

	/**
	* is the player paused?
	*
	* This is the players' target playback state. This does not mean it is currently
	* displaying video, and if it is false, it does not mean that it is currently playing
	* video.
	*
	* @emits {PLAYBACK_PAUSED} when the player actually pauses
	* @emits {PLAYBACK_PLAYING} when the player actually begins playing
	*
	* @since 0.0.3
	* @type {boolean}
	*/
	get isPaused() {
		return this._controller.paused;
	}

	/**
	* is the player buffering?
	*
	* Note, the player can still be buffering, but will return false if it is paused.
	*
	* @emits {PLAYBACK_BUFFERING} when buffering starts
	* @emits {STREAM_BUFFERING_FINISHED} when the buffering finishes
	*
	* @since 0.2
	* @type {boolean}
	*/
	get isBuffering() {
		return this._controller.buffering;
	}

	/**
	* is the player seeking?
	*
	* @emits {PLAYBACK_SEEKING} when seeking starts
	* @emits {STREAM_SEEKING_FINISHED} when seeking finishes
	*
	* @since 0.2
	* @type {boolean}
	*/
	get isSeeking() {
		return this._controller.seeking;
	}

	/**
	* has playback reached the end of the content?
	*
	* Will remain true after reaching the end until the source is reloaded/changed
	* or a seek is performed.
	*
	* @emits {PLAYBACK_ENDED} when the end is reached
	*
	* @since 0.0.3
	* @type {boolean}
	*/
	get isEnded() {
		return this._controller.ended;
	}

	/**
	* is the stream live?
	*
	* A live stream is a stream which is being actively appended with new content.
	* As the stream is appended to the duration will change. Eventually the stream
	* may become not-live.
	*
	* A live stream may or may not be sliding window. Use isSlidingWindow to tell
	*
	* All streams are assumed to be live when they are first loaded. This is
	* because a non-live stream can never transition to being live. This means that
	* when the stream is first loaded, isLive will return true, when it later
	* becomes known to be either live or not live, STREAM_ISLIVE_CHANGED will fire.
	* In the case of a stream loading in a live state this means that the live and
	* lastLive properties of the update event will both be true. Internally
	* lastLive was undefined until the time the event was fired.
	*
	* In effect, this means you do not need to poll isLive, but rather can rely on
	* the event being fired when the isLive property becomes known, or is updated.
	*
	* @emits {STREAM_ISLIVE_CHANGED} when the isLive property becomes
	* known or is updated. data: {live: , lastLive };
	*
	* @since 0.4
	* @type {boolean}
	*/
	get isLive() {
		return this._controller.live;
	}

	/**
	* does the stream have a sliding window?
	*
	* A sliding window stream is a live stream where content is being removed as
	* more content is added. Thus the window of available content slides. Generally
	* this is the concept that most people think of when they think of live
	* streaming video. If the stream isLive but is not isSlidingWindow then it is
	* an event stream, ie a live stream that you can seek to any point in.
	*
	* NOTE: live streams may start out as non sliding window streams until content
	* is removed from the stream, then they become sliding window streams.
	*
	* @emits {STREAM_ISSLIDINGWINDOW_CHANGED} when the isSlidingWindow property becomes
	* known or is updated. data: {slidingWindow: , lastSlidingWindow };
	*
	* @since 0.4
	* @type {boolean}
	*/
	get isSlidingWindow() {
		return this._controller.slidingWindow;
	}

	/**
	 * Is the stream audio only?
	 *
	 * Note that this checks if all variants of the stream are audio only. If one variant is audio only but other
	 * variants contain video, this will return false.
	 *
	 * @since 1.2
	 * @type {boolean}
	 */
	get isAudioOnly() {
		return this._controller.audioOnly;
	}

	/**
	 * Is BAMHLS operating in multiple threads?
	 *
	 * When possible, a separate worker thread will be created for demuxing/remuxing. At start, this will return wether
	 * or not it is possible to have a worker thread. Once demuxing has begun, this will return wether or not the
	 * worker thread exists.
	 *
	 * @emits {STREAM_ISMULTITHREADED_CHANGED} if isMultithreaded changes. data: {multithreaded, lastMultithreaded};
	 *
	 * @since 1.8
	 * @type {boolean}
	 */
	get isMultithreaded() {
		return this._controller.multithreaded;
	}

	/**
	 * The seekable range of the stream
	 *
	 * A stream's seekable range is a range of times in which a fragment is currently loadable. In VOD and live event
	 * streams, the range is the entire stream, however in sliding window streams, the range is limited by the duration
	 * of the sliding window, and the user will not be able to seek outside this range.
	 *
	 * Returns an object containing the start, the end, and the total duration of the seekable range, all three
	 * measured in seconds.
	 *
	 * @since 1.1
	 * @type {object} { start: number, end: number, duration: number }
	 */
	get seekableRange() {
		return this._controller.seekableRange;
	}

	/**
	 * All buffered ranges of the stream
	 *
	 * A stream's buffered range is a range of times in which fragments have been added to the buffer. If a specified
	 * time is within a buffered range and the playback range, seeking to that time should play as soon as the seek
	 * finishes.
	 *
	 * The buffered range is based on the audio buffer when possible, the video buffer may have slight differences.
	 *
	 * Returns an array of objects that contain the start, the end, and the total duration of each buffered range,
	 * all three measured in seconds.
	 *
	 * @since 1.9
	 * @type {object[]} { start: number, end: number, duration: number }
	 */
	get bufferedRange() {
		return this._controller.bufferedRange;
	}

	/**
	 * The buffered range the current time is located in.
	 *
	 * A stream may have multiple buffered ranges. This property returns the buffered range that the player is
	 * currently in.
	 *
	 * The buffered range is based on the audio buffer when possible, the video buffer may have slight differences.
	 *
	 * Returns an object that contains the start, the end, and the total duration of the current buffered range, all
	 * three measured in seconds.
	 *
	 * @since 1.9
	 * @type {object} { start: number, end: number, duration: number }
	 */
	get bufferedRangeCurrent() {
		return this._controller.bufferedRangeCurrent;
	}

	/**
	 * The playback range of the stream
	 *
	 * A stream's playback range is a range of times that the stream will play through without interruption. The start
	 * point of the range is the current time, and it extends to the end of the seekable or current buffered range,
	 * whichever comes first.
	 *
	 * Returns an object that contains the start, the end, and the total duration of the playback range, all three
	 * measured in seconds.
	 *
	 * @since 1.9
	 * @type {object} { start: number, end: number, duration: number }
	 */
	get playbackRange() {
		return this._controller.playbackRange;
	}

	/**
	* first PDT specified by the current manifest. Will be null if none is in manifest.
	*
	* @since 0.7
	* @type {Date}
	*/
	get basePdt() {
		return this._controller.basePdt;
	}

	/**
	* the current duration of the content, in seconds
	*
	* Note: this may continue to increase sporadically for live content
	*
	* @emits {STREAM_DURATION_CHANGED} when the duration changes
	*
	* @since 0.0.6
	* @type {number}
	*/
	get duration() {
		return this._controller.duration;
	}

	/**
	* the initial time that playback started at in seconds. This is related to startTime
	*
	* Note: this may not exactly equal startTime.
	*
	* @since 0.0.6
	* @type {number}
	*/
	get initialTime() {
		return this._controller.initialTime;
	}

	/**
	* the current live time in seconds. ie the maximum seekable time in the stream that is regarded as the
	* live point.
	*
	* Note: this will be less than the duration, as the liveTime takes account of the liveBuffer
	*
	* Note: this value defaults to zero, and will only be initialised once it is know. This will occur
	* when the variants are loaded. It is sufficient to listen to the event for changes.
	*
	* It will only ever increase.
	*
	* @emits {STREAM_LIVETIME_CHANGED} when the live time changes. data: {liveTime, lastLiveTime };
	*
	* @since 0.4
	* @type {number}
	*/
	get liveTime() {
		return this._controller.liveTime;
	}

	/**
	* the variants array, ie bitrate profiles or levels
	*
	* The variants array contains each variant in the manifest sorted by
	* ascending bitrate with various interesting properties.
	*
	* A variant is a version of the stream which is encoded at a specific bitrate
	*
	* @emits {STREAM_VARIANTS_CHANGED} when changed/updated/available.
	*
	* @since 0.3
	* @type {array} -
	*/
	get variants() {
		return this._controller.variants;
	}

	/**
	 * the audio renditions array
	 *
	 * The audio rendition array contains each audio language in the mainfest. Tracks with the same name are grouped together into a single rendition.
	 *
	 * Renditions are assigned a numerical ID when added to the rendition list. This number is what is used with the currentAudioRendition property.
	 *
	 * @since 2.8
	 * @type {array}
	 */
	get audioRenditions() {
		return this._controller.audioRenditions;
	}

	/**
	 * currently selected audio rendition
	 *
	 * If there are multiple tracks with the selected rendition, the track with the same audio rendition group as the currently selected variant
	 * will play.
	 *
	 * @since 2.8
	 * @type {integer}
	 */

	get currentAudioRendition() {
		return this._controller.currentAudioRendition;
	}

	set currentAudioRendition(index) {
		this._controller.currentAudioRendition = index;
	}



	/**
	 * default audio rendition language
	 *
	 * When renditions are valid for the first time this will be set if possible.
	 * Compares this with the lang value of the audioRenditions array
	 *
	 * @since 2.9
	 * @type {string}
	 */
	get initialAudioLanguage() {
		return this._controller.initialAudioLanguage;
	}

	set initialAudioLanguage(language) {
		this._controller.initialAudioLanguage = language;
	}



	/**
	 * default audio rendition name
	 *
	 * When renditions are valid for the first time this will be set if possible.
	 * Compares this with the name value of the audioRenditions array
	 *
	 * @since 2.9
	 * @type {string}
	 */
	get initialAudioName() {
		return this._controller.initialAudioName;
	}

	set initialAudioName(name) {
		this._controller.initialAudioName = name;
	}



	/**
	 * default audio rendition name
	 *
	 * When renditions are valid for the first time this will be set if possible.
	 * Compares this with the name value of the audioRenditions array
	 *
	 * @since 2.9
	 * @type {string}
	 */
	get initialAudioIndex() {
		return this._controller.initialAudioIndex;
	}

	set initialAudioIndex(index) {
		this._controller.initialAudioIndex = index;
	}


	/**
	* the subtitle renditions array, ie the available subtitle/caption languages
	*
	* The Subtitle Renditions is a list of all the available subtitle renditions
	* found in the media
	*
	* @emits {STREAM_SUBTITLERENDITIONS_CHANGED} when changed/updated/available
	*
	* @since 0.3
	* @type {array}
	*/
	get subtitleRenditions() {
		return this._controller.subtitleRenditions;
	}

	/**
	 * current muted state of the media
	 *
	 * isMuted returns true if playback is currently muted, ie, isMuted has been set true
	 * or volume is set to 0.0. If isMuted is set, then volume will return 0.0 until
	 * isMuted is set to false, then volume will return its previous value.
	 *
	 * @since 0.0.3
	 * @type {boolean}
	 */
	get isMuted() {
		return this._controller.muted;
	}

	set isMuted(value) {
		this._controller.muted = value;
	}


	/**
	 * current volume of the media (0.0 - 1.0)
	 *
	 * The volume applied to the media. Valid ranges are 0.0 to 1.0 (ie 100%)
	 *
	 * Setting volume to 0.0 will cause muted to return true, until the volume is
	 * not 0.0.
	 *
	 * Setting muted to true will cause volume to evaluate as 0.0 until muted is
	 * is set false, and the volume is not 0.0
	 *
	 * @since 0.0.3
	 * @type {float}
	 */
	get volume() {
		return this._controller.volume;
	}

	set volume(value) {
		this._controller.volume = value;
	}

	/**
	 * current constraints used for variant selection
	 *
	 * This object contains minimum and maximum values for variant number, bitrate (in bps), width and height (both in
	 * pixels). A value of -1 indicates that that constraint is not being used.
	 *
	 * The variant selector will try to follow as many of these constraints as possible, but will ignore constraints
	 * if it determines that following them is impossible, such as if a minimum constraint exceeds a maximum, there is
	 * no variant between the minimum or maximum, or if no level satisfies a constraint. Minimum constraints will be
	 * ignored before maximum constraints.
	 *
	 * If it has been determined that the overall maximum variant constraint has been relaxed, the buffer will flush
	 * fragments that have a lower bitrate than the one currently being loaded. Note that backfill is not currently
	 * implemented so seeking backwards after the flush may cause stalls. This flush can be cancelled by setting noFlush
	 * to true.
	 *
	 * width and height constraints will currently only be considered if the stream has explicitly defined dimensions.
	 *
	 * @since 1.9
	 * @type {object} { minVariant:integer, maxVariant:integer, minBitrate:integer, maxBitrate:integer,
	 * minWidth:integer, minHeight:integer, maxWidth:integer, maxHeight:integer, noFlush:boolean }
	 */
	getConstraints() {
		return this._controller.getConstraints();
	}

	setConstraints(constraints = {}) {
		this._controller.setConstraints(constraints);
	}

	/**
	 * current timeouts
	 *
	 * @since 1.15
	 * @type {object} { manifestTimeout:integer, manifestTimeoutMax:integer, variantTimeout:integer,
	 * variantTimeoutMax:integer, chunkTimeout:integer, chunkTimeoutMax:integer, streamTimeout:integer }
	 */
	getTimeouts() {
		return this._controller.getTimeouts();
	}

	setTimeouts(timeouts = {}) {
		this._controller.setTimeouts(timeouts);
	}

	/**
	 * If true, this will attempt to simplify the stream when performance issues are detected. For example,
	 * it will lower the maximum bitrate variant to below the one that is currently playing if more than 20% of frames have been dropped.
	 *
	 * Defaults to false. Changes will apply upon switching video or media element. This property has no effect when running on a platform that does not track
	 * number of frames decoded/dropped (known supported platforms with this issue: all Tizen versions).
	 *
	 * @since 2.10
	 * @type {boolean}
	 */
	get useAutomaticComplexityReduction() {
		return this._controller.useAutomaticComplexityReduction;
	}

	set useAutomaticComplexityReduction(value) {
		this._controller.useAutomaticComplexityReduction = !!value;
	}

	/**
	 * If true, cycles the bitrate up and down between the minimum and maximum variant constraints.
	 *
	 * Variant changes done this way will not flush the buffer.
	 *
	 * @since 1.10
	 * @type {boolean}
	 */
	get bitrateCycle() {
		return this._controller.bitrateCycle;
	}

	set bitrateCycle(value) {
		this._controller.bitrateCycle = value;
	}

	/**
	 * Size of the stream buffer, in bytes. Default is 60000000, or 60 megabytes.
	 *
	 * This is used with the current bitrate to determine the length of the buffer in seconds.
	 *
	 * @since 0.10
	 * @type {number}
	 */
	get maximumBufferSize() {
		return this._controller.maximumBufferSize;
	}

	set maximumBufferSize(value) {
		this._controller.maximumBufferSize = value;
	}

	/**
	 * Minimum stream buffer length, in seconds. Default is 30 seconds.
	 *
	 * maximumBufferSeconds will override this constraint, if necessary.
	 *
	 * 1 is the minimum allowed value, as lower buffer lengths may prevent the stream from buffering at all.
	 *
	 * @since 0.10
	 * @type {number}
	 */
	get minimumBufferSeconds() {
		return this._controller.minimumBufferLength;
	}

	set minimumBufferSeconds(value) {
		this._controller.minimumBufferLength = value;
	}

	/**
	 * Maximum stream buffer length, in seconds. Default is 60 seconds.
	 *
	 * This constraint will override minimumBufferSeconds, if necessary.
	 *
	 * 1 is the minimum allowed value, as lower buffer lengths may prevent the stream from buffering at all.
	 *
	 * @since 0.10
	 * @type {number}
	 */
	get maximumBufferSeconds() {
		return this._controller.maximumBufferLength;
	}

	set maximumBufferSeconds(value) {
		this._controller.maximumBufferLength = value;
	}

	/**
	* are subtitles being rendered on the video element. If there are no subtitles to
	* display, then there will be no subtitles to display.
	*
	* setting to true will cause subtitles to display if they are available
	*
	* @since 0.3
	* @type {bool}
	*/
	get areSubtitlesEnabled() {
		return this._controller.subtitlesEnabled;
	}

	set areSubtitlesEnabled(value) {
		this._controller.subtitlesEnabled = value;
	}

	/**
	* Changes the output of the video element to the requested
	* language if possible. If not possible the caption selection will be unchanged.
	*
	* @since 0.6
	* @type {integer}
	*/
	set changeSubtitlesByIndex(value) {
		this._controller.changeSubtitlesByIndex = value;
	}

	/**
	* a callback which is called after creating an XHR request to allow modification of
	* the request. Ie to enable cookie support, or add RequestHeaders.
	*
	* @example
	* stream.xhrSetupCallback = function (xhr, url, type) {
	*     xhr.withCredentials = true; // do send cookies
	* }),
	*
	* null means no function is defined. Use null to clear.
	*
	* @since 0.7
	* @type {function} function(xhr: XMLHttpRequest, url: string, type: DataTypes)} Xhr Setup Function
	*/
	get xhrSetupCallback() {
		return this._controller.xhrSetup;
	}

	set xhrSetupCallback(func) {
		this._controller.xhrSetup = func;
	}

	/**
	* a callback which, if not null, will be called to acquire a key
	*
	* The callback will be supplied with context and callbacks parameters. It *must*
	* call either onSuccess, onError or onTimeout methods in the callbacks parameter.
	*
	* The context.url property is the url for the key. The context.frag property is
	* the fragment that is requesting the key.
	*
	* @example
	* stream.keyLoadCallback = function (context, callbacks) {
	*     let result = myGetKeyFunction(context.url);
	*     switch(result.code) {
	*     case 'SUCCESS':
	*         callbacks.onSuccess(result.key);
	*         break;
	*     case 'ERROR':
	*         callbacks.onError(result.response, result.error);
	*         break;
	*     case 'TIMEOUT':
	*         callbacks.onTimeout();
	*     }
	* }),
	*
	* null means no function is defined. Use null to clear.
	*
	* @since 0.9
	* @type {function} keyLoadCallback(context,callbacks)
	*/
	get keyLoadCallback() {
		return this._controller.keyLoadCallback;
	}

	set keyLoadCallback(func) {
		this._controller.keyLoadCallback = func;
	}

	/**
	 * A callback which when called will return a KeySystemConfig
	 *
	 * @since 3.2
	 * @type {function} obtainKeySystemConfigCallback( keySystem, keySystemData )
	 */
	get obtainKeySystemConfigCallback() {
		return this._controller.obtainKeySystemConfigCallback;
	}
	set obtainKeySystemConfigCallback(func) {
		this._controller.obtainKeySystemConfigCallback = func;
	}

	/**
	* If true then dispose has been called already, and the HlsStream has either
	* already been disposed, or is in the process of disposing.
	*
	* @since 0.8
	* @type {boolean}
	*/
	get isDisposed() {
		return !!this._disposed;
	}

	/**
	* If true then dispose has been called, and the implementation has been destroyed.
	* Further attempts to access any methods on HlsStream will fail.
	*
	* @since 3.2
	* @type {boolean}
	*/
	get isDestroyed() {
		return !this._controller;
	}

	/**
	 * Reference Id for this stream
	 * Increases each new stream
	 *
	 * @since 2.9
	 *  @type {integer}
	 */
	get streamId() {
		return this._controller.instanceId;
	}

	/**
	* If true then console.log will be used to output debug trace information
	*
	* @since 0.1
	* @type {boolean}
	*/
	get debug() {
		return this._controller.debug;
	}

	set debug(value) {
		this._controller.debug = value;
	}

	/**
	 * returns an object containing various playback metrics
	 *
	 * HlsPlaybackMetrics{
	 *     time,				// currentTime
	 *     droppedFramesCount,	// media.getVideoPlaybackQuality().droppedVideoFrames or media.webkitDroppedFrameCount
	 *     bitrate,				// chosenBitrate
	 *     bufferedRange,		// bufferedRangeCurrent {start,end,duration}
	 *     playbackRange,		// {start,end,duration}
	 *     seekableRange, 		// manifest window {start,end,duration}
	 * }
	 *
	 * @since 1.3
	 * @returns {HlsPlaybackMetrics}
	 */
	getPlaybackMetrics() {
		return this._controller.getPlaybackMetrics();
	}

	/**
	 * returns an object containing various network metrics
	 *
	 * HlsNetworkMetrics{
	 *     chosenVariant,		// index of currently loading stream variant
	 *     chosenBitrate,		// bitrate of currently loading stream variant
	 *     perceivedBitrate,	// estimate of the current available network bandwith
	 * }
	 *
	 * @since 3.2
	 * @returns {HlsNetworkkMetrics}
	 */
	getNetworkMetrics() {
		return this._controller.getNetworkMetrics();
	}

	/**
	 * returns a promise to a copy of the DrmCapabilities
	 * DrmCapabilities is a list of the supported status and capabilities for all KeySystems specified in the KeySystems enum
	 */
	getDrmCapabilities() {
		return this._controller.getDrmCapabilities();
	}

	/**
	 * Sets a callback to be called at the specified timeline time with an ID being the return value.
	 * The callback will in the format of (time, data, id) to enable you to match IDs
	 * or remove as needed.
	 *
	 * @since 2.8
	 * @returns {integer}
	 */
	addMarker(time, callback, data) {
		return this._controller.addUserMarker(time, callback, data);
	}

	/**
	 * Removes specified ID from marker list. IDs can be obtained from the addMarker
	 * method as well as addMarker callbacks.
	 *
	 * @since 2.8
	 */
	removeMarker(id) {
		this._controller.removeMarker(id);
	}


	/**
	 * applies the supplied config object to the HlsStream instance.
	 *
	 * All properties in the config which match an available setters on the instance
	 * will be applied.
	 *
	 * @example
	 * hls.applyConfig({
	 *     autoPlay: true,
	 *     debug: true,
 	 * });
 	 *
	 * @since 0.1
	 * @param {Object} [config={}] - configuration obect
	 */
	applyConfig(config = {}) {
		Utils.applyConfig(this, config);
	}

	/**
	 * Converts a given date into a timestamp for the currently loaded stream.
	 *
	 * This function is able to return timestamps that are outside of the seekable range.
	 *
	 * Returns null if the stream does not have a base PDT
	 *
	 * @since 2.12
	 * @param {Date} pdt
	 * @returns {number}
	 */
	getTimeForPdt(pdt) {
		return this._controller.getTimeForPdt(pdt);
	}

	/**
	 * Converts a given timestamp into a PDT for the currently loaded stream.
	 *
	 * This function is able to use timestamps that are outside of the seekable range.
	 *
	 * Returns null if the stream does not have a base PDT
	 *
	 * @since 2.12
	 * @param {number} time
	 * @returns {Date}
	 */
	getPdtForTime(time) {
		return this._controller.getPdtForTime(time);
	}

	/**************************
		SOFT DEPRECATIONS
	 **************************/

	/**
	 * synonym for getNetworkMetrics().chosenVariant, for compatibility with pre-3.2
	 *
	 * @deprecated use getNetworkMetrics()
	 * @since 0.9
	 * @type {integer}
	 */
	get chosenVariant() {
		return this.getNetworkMetrics().chosenVariant;
	}

	/**
	 * synonym for getNetworkMetrics().chosenBitrate, for compatibility with pre-3.2
	 *
	 * @deprecated use getNetworkMetrics()
	 * @since 0.9
	 * @type {integer}
	 */
	get chosenBitrate() {
		return this.getNetworkMetrics().chosenBitrate;
	}

	/**************************
		HARD DEPRECATIONS
	 **************************/

	/**
	* @ignore use getConstraints and setConstraints
	*/
	get maximumBitrate() {
		throw new Error('maximumBitrate is no longer supported');
	}

	set maximumBitrate(value) {
		throw new Error('maximumBitrate is no longer supported');
	}


	/**
	* @ignore use getConstraints and setConstraints
	*/
	get minimumBitrate() {
		throw new Error('minimumBitrate is no longer supported');
	}

	set minimumBitrate(value) {
		throw new Error('minimumBitrate is no longer supported');
	}

	/**
	* @ignore use getConstraints and setConstraints
	*/
	get maximumVariant() {
		throw new Error('maximumVariant is no longer supported');
	}

	set maximumVariant(value) {
		throw new Error('maximumVariant is no longer supported');
	}

	/**
	* @ignore use getConstraints and setConstraints
	*/
	get minimumVariant() {
		throw new Error('minimumVariant is no longer supported');
	}

	set minimumVariant(value) {
		throw new Error('minimumVariant is no longer supported');
	}

	/**
	* @ignore use getConstraints and setConstraints
	*/
	get maximumWidth() {
		throw new Error('maximumWidth is no longer supported');
	}

	set maximumWidth(value) {
		throw new Error('maximumWidth is no longer supported');
	}

	/**
	* @ignore use getConstraints and setConstraints
	*/
	get maximumHeight() {
		throw new Error('maximumHeight is no longer supported');
	}

	set maximumHeight(value) {
		throw new Error('maximumHeight is no longer supported');
	}

}

export default HlsStream;



// WEBPACK FOOTER //
// ./src/HlsStream.js